export const BOARD_SIZE = 5;

export type CardinalDirection = "north" | "east" | "south" | "west";

export type Position = {
  readonly row: number;
  readonly column: number;
};

export type Vector = {
  readonly row: number;
  readonly column: number;
};

export type OutsidePosition = Position & {
  readonly kind: "outside";
};

export type VisualCell =
  { readonly kind: "gap" } | { readonly kind: "slab"; readonly discCount: number };

export type VisualInventory = readonly (readonly VisualCell[])[];

export type ExtractionPlan =
  | { readonly kind: "edge"; readonly direction: CardinalDirection }
  | { readonly kind: "gap"; readonly direction: CardinalDirection }
  | {
      readonly kind: "push";
      readonly direction: CardinalDirection;
      readonly positions: readonly Position[];
      readonly opening: Position | OutsidePosition;
    }
  | { readonly kind: "lift" };

export type ExtractionRequest = {
  readonly inventory: VisualInventory;
  readonly target: Position;
  readonly departureVector?: Vector;
};

export const CARDINAL_DIRECTIONS: readonly CardinalDirection[] = ["north", "east", "south", "west"];

export const CARDINAL_VECTORS: Readonly<Record<CardinalDirection, Vector>> = {
  north: { row: -1, column: 0 },
  east: { row: 0, column: 1 },
  south: { row: 1, column: 0 },
  west: { row: 0, column: -1 },
};

type PushCandidate = {
  readonly direction: CardinalDirection;
  readonly positions: readonly Position[];
  readonly opening: Position | OutsidePosition;
  readonly openingKind: "gap" | "outside";
};

const directionRanks: Readonly<Record<CardinalDirection, number>> = {
  north: 0,
  east: 1,
  south: 2,
  west: 3,
};

export function planExtraction(request: ExtractionRequest): ExtractionPlan;
export function planExtraction(request: unknown): ExtractionPlan {
  validateRequest(request);

  const borderDirections = CARDINAL_DIRECTIONS.filter((direction) =>
    isOutside(step(request.target, direction)),
  );
  if (borderDirections.length > 0) {
    return {
      kind: "edge",
      direction: chooseDirection(borderDirections, request.departureVector),
    };
  }

  const gapDirections = CARDINAL_DIRECTIONS.filter((direction) => {
    const adjacent = step(request.target, direction);
    return getCell(request.inventory, adjacent).kind === "gap";
  });
  if (gapDirections.length > 0) {
    return {
      kind: "gap",
      direction: chooseDirection(gapDirections, request.departureVector),
    };
  }

  const candidates = CARDINAL_DIRECTIONS.map((direction) =>
    buildPushCandidate(request.inventory, request.target, direction),
  ).filter((candidate): candidate is PushCandidate => candidate !== null);

  if (candidates.length === 0) {
    return { kind: "lift" };
  }

  const candidate = [...candidates].sort((left, right) =>
    comparePushCandidates(left, right, request.departureVector),
  )[0];
  if (candidate === undefined) {
    throw new Error("An eligible push candidate was unexpectedly unavailable.");
  }

  return {
    kind: "push",
    direction: candidate.direction,
    positions: candidate.positions,
    opening: candidate.opening,
  };
}

function validateRequest(request: unknown): asserts request is ExtractionRequest {
  if (!isRecord(request)) {
    throw new TypeError("Extraction request must be an object.");
  }

  const inventory = request["inventory"];
  const target = request["target"];
  const departureVector = request["departureVector"];
  validateInventory(inventory);
  validatePosition(target, "Target");

  if (getCell(inventory, target).kind === "gap") {
    throw new RangeError("Target must identify an existing slab.");
  }

  if (departureVector !== undefined) {
    validateVector(departureVector);
  }
}

function validateInventory(inventory: unknown): asserts inventory is VisualInventory {
  if (!isUnknownArray(inventory) || inventory.length !== BOARD_SIZE) {
    throw new RangeError(`Visual inventory must contain exactly ${BOARD_SIZE} rows.`);
  }

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    const cells = inventory[row];
    if (!isUnknownArray(cells) || cells.length !== BOARD_SIZE) {
      throw new RangeError(`Visual inventory row ${row} must contain exactly ${BOARD_SIZE} cells.`);
    }

    for (let column = 0; column < BOARD_SIZE; column += 1) {
      if (!(column in cells)) {
        throw new TypeError(`Visual inventory cell (${row}, ${column}) is missing.`);
      }
      validateCell(cells[column], row, column);
    }
  }
}

function validateCell(cell: unknown, row: number, column: number): asserts cell is VisualCell {
  if (!isRecord(cell)) {
    throw new TypeError(`Visual inventory cell (${row}, ${column}) is invalid.`);
  }

  if (cell["kind"] === "gap") {
    return;
  }

  const discCount = cell["discCount"];
  if (
    cell["kind"] !== "slab" ||
    typeof discCount !== "number" ||
    !Number.isInteger(discCount) ||
    discCount < 0 ||
    discCount > 3
  ) {
    throw new RangeError(
      `Visual inventory slab (${row}, ${column}) must have an integer disc count from 0 to 3.`,
    );
  }
}

function validatePosition(position: unknown, label: string): asserts position is Position {
  if (
    !isRecord(position) ||
    typeof position["row"] !== "number" ||
    typeof position["column"] !== "number" ||
    !Number.isInteger(position["row"]) ||
    !Number.isInteger(position["column"]) ||
    position["row"] < 0 ||
    position["row"] >= BOARD_SIZE ||
    position["column"] < 0 ||
    position["column"] >= BOARD_SIZE
  ) {
    throw new RangeError(`${label} must be an integer position inside the 5x5 board.`);
  }
}

function validateVector(vector: unknown): asserts vector is Vector {
  if (
    !isRecord(vector) ||
    typeof vector["row"] !== "number" ||
    typeof vector["column"] !== "number" ||
    !Number.isInteger(vector["row"]) ||
    !Number.isInteger(vector["column"]) ||
    Math.abs(vector["row"]) > 1 ||
    Math.abs(vector["column"]) > 1 ||
    (vector["row"] === 0 && vector["column"] === 0)
  ) {
    throw new RangeError("Departure vector must describe one non-zero adjacent step.");
  }
}

function buildPushCandidate(
  inventory: VisualInventory,
  target: Position,
  direction: CardinalDirection,
): PushCandidate | null {
  const positions: Position[] = [];
  let current = target;

  while (!isOutside(current)) {
    const cell = getCell(inventory, current);
    if (cell.kind === "gap") {
      return {
        direction,
        positions,
        opening: current,
        openingKind: "gap",
      };
    }

    if (positions.length > 0 && cell.discCount > 0) {
      return null;
    }

    positions.push(current);
    current = step(current, direction);
  }

  return {
    direction,
    positions,
    opening: { kind: "outside", row: current.row, column: current.column },
    openingKind: "outside",
  };
}

function comparePushCandidates(
  left: PushCandidate,
  right: PushCandidate,
  departureVector: Vector | undefined,
): number {
  const costDifference = left.positions.length - right.positions.length;
  if (costDifference !== 0) {
    return costDifference;
  }

  if (departureVector !== undefined) {
    const dotDifference = compareDotProducts(left.direction, right.direction, departureVector);
    if (dotDifference !== 0) {
      return dotDifference;
    }
  }

  const openingDifference = openingRank(left.openingKind) - openingRank(right.openingKind);
  if (openingDifference !== 0) {
    return openingDifference;
  }

  return directionRanks[left.direction] - directionRanks[right.direction];
}

function chooseDirection(
  directions: readonly CardinalDirection[],
  departureVector: Vector | undefined,
): CardinalDirection {
  const direction = [...directions].sort((left, right) =>
    compareDirections(left, right, departureVector),
  )[0];
  if (direction === undefined) {
    throw new Error("At least one extraction direction is required.");
  }
  return direction;
}

function compareDirections(
  left: CardinalDirection,
  right: CardinalDirection,
  departureVector: Vector | undefined,
): number {
  if (departureVector !== undefined) {
    const dotDifference = compareDotProducts(left, right, departureVector);
    if (dotDifference !== 0) {
      return dotDifference;
    }
  }

  return directionRanks[left] - directionRanks[right];
}

function compareDotProducts(
  left: CardinalDirection,
  right: CardinalDirection,
  departureVector: Vector,
): number {
  return (
    dot(CARDINAL_VECTORS[left], departureVector) - dot(CARDINAL_VECTORS[right], departureVector)
  );
}

function dot(left: Vector, right: Vector): number {
  return left.row * right.row + left.column * right.column;
}

function openingRank(kind: PushCandidate["openingKind"]): number {
  return kind === "gap" ? 0 : 1;
}

function step(position: Position, direction: CardinalDirection): Position {
  const vector = CARDINAL_VECTORS[direction];
  return {
    row: position.row + vector.row,
    column: position.column + vector.column,
  };
}

function isOutside(position: Position): boolean {
  return (
    position.row < 0 ||
    position.row >= BOARD_SIZE ||
    position.column < 0 ||
    position.column >= BOARD_SIZE
  );
}

function getCell(inventory: VisualInventory, position: Position): VisualCell {
  const cell = inventory[position.row]?.[position.column];
  if (cell === undefined) {
    throw new RangeError(
      `No visual inventory cell exists at (${position.row}, ${position.column}).`,
    );
  }
  return cell;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

function isUnknownArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}
